// Mock data for Teachers Management page

export interface Teacher {
  id: string;
  personalInfo: {
    firstName: string;
    lastName: string;
    email: string;
    employeeId: string;
    gender: 'male' | 'female' | 'other';
  };
  employment: {
    contractType: 'full-time' | 'part-time' | 'supply' | 'temporary';
    startDate: string;
    teachingCapacity: number;
    maxPeriodsPerDay: number;
    totalContactTime: number;
    salaryScale: string;
  };
  roles: {
    primaryRole: string;
    additionalRoles: string[];
    isHOD: boolean;
    isSLT: boolean;
    responsibilities: string[];
  };
  availability: {
    workingDays: {
      [day: string]: {
        available: boolean;
        startTime?: string;
        endTime?: string;
        unavailablePeriods?: string[];
      };
    };
    constraints: {
      noBackToBackClasses?: boolean;
      maxConsecutivePeriods?: number;
      preferredBreakTimes?: string[];
      travelTimeBetweenRooms?: number;
    };
  };
  qualifications: {
    subjectId: string;
    subjectName: string;
    level: 'primary' | 'secondary' | 'tertiary';
    certification: string;
    dateObtained: string;
    isActive: boolean;
    canTeachYearGroups: string[];
    confidenceLevel: 1 | 2 | 3 | 4 | 5;
  }[];
  preferences: {
    preferredSubjects: string[];
    preferredYearGroups: string[];
    preferredTimeSlots: string[];
    preferredRooms: string[];
    avoidTimeSlots: string[];
    workloadPreference: 'light' | 'moderate' | 'heavy';
    coTeachingWillingness: boolean;
    mentorshipAvailability: boolean;
  };
}

export interface TeacherAssignment {
  id: string;
  teacherId: string;
  teacherName: string;
  subjectId: string;
  subjectName: string;
  yearGroup: string;
  classGroup: string;
  assignmentType: 'primary' | 'secondary' | 'backup' | 'co-teacher';
  periodsPerWeek: number;
  roomName?: string;
  effectiveDate: string;
}

export interface WorkloadSummary {
  teacherId: string;
  teacherName: string;
  totalAssignedPeriods: number;
  contractualPeriods: number;
  utilizationPercentage: number;
  status: 'underloaded' | 'optimal' | 'overloaded';
  subjectBreakdown: {
    subjectId: string;
    periods: number;
    yearGroups: string[];
  }[];
}

export const mockData = {
  teachers: [
    {
      id: 'T001',
      personalInfo: {
        firstName: 'Sarah',
        lastName: 'Johnson',
        email: 'sarah.johnson@school.edu',
        employeeId: 'EMP001',
        gender: 'female' as const,
      },
      employment: {
        contractType: 'full-time' as const,
        startDate: '2020-09-01',
        teachingCapacity: 22,
        maxPeriodsPerDay: 5,
        totalContactTime: 18,
        salaryScale: 'M6',
      },
      roles: {
        primaryRole: 'Head of Mathematics',
        additionalRoles: ['GCSE Examiner', 'Numeracy Coordinator'],
        isHOD: true,
        isSLT: false,
        responsibilities: ['Department Leadership', 'Curriculum Development', 'Staff Mentoring'],
      },
      availability: {
        workingDays: {
          Monday: { available: true, startTime: '08:00', endTime: '16:00' },
          Tuesday: { available: true, startTime: '08:00', endTime: '16:00' },
          Wednesday: { available: true, startTime: '08:00', endTime: '16:00' },
          Thursday: { available: true, startTime: '08:00', endTime: '16:00' },
          Friday: { available: true, startTime: '08:00', endTime: '15:00' },
        },
        constraints: {
          noBackToBackClasses: false,
          maxConsecutivePeriods: 3,
          preferredBreakTimes: ['10:45', '13:00'],
          travelTimeBetweenRooms: 5,
        },
      },
      qualifications: [
        {
          subjectId: 'MATH',
          subjectName: 'Mathematics',
          level: 'secondary' as const,
          certification: 'PhD Mathematics',
          dateObtained: '2018-06-15',
          isActive: true,
          canTeachYearGroups: ['Year 7', 'Year 8', 'Year 9', 'Year 10', 'Year 11'],
          confidenceLevel: 5,
        },
        {
          subjectId: 'STAT',
          subjectName: 'Statistics',
          level: 'tertiary' as const,
          certification: 'MSc Statistics',
          dateObtained: '2016-07-20',
          isActive: true,
          canTeachYearGroups: ['Year 10', 'Year 11'],
          confidenceLevel: 5,
        },
      ],
      preferences: {
        preferredSubjects: ['Mathematics', 'Statistics'],
        preferredYearGroups: ['Year 10', 'Year 11'],
        preferredTimeSlots: ['Period 1', 'Period 2', 'Period 3'],
        preferredRooms: ['M01', 'M02'],
        avoidTimeSlots: ['Period 6'],
        workloadPreference: 'heavy' as const,
        coTeachingWillingness: true,
        mentorshipAvailability: true,
      },
    },
    {
      id: 'T002',
      personalInfo: {
        firstName: 'James',
        lastName: 'Wilson',
        email: 'james.wilson@school.edu',
        employeeId: 'EMP002',
        gender: 'male' as const,
      },
      employment: {
        contractType: 'part-time' as const,
        startDate: '2021-01-15',
        teachingCapacity: 16,
        maxPeriodsPerDay: 4,
        totalContactTime: 14,
        salaryScale: 'M3',
      },
      roles: {
        primaryRole: 'English Teacher',
        additionalRoles: ['Form Tutor'],
        isHOD: false,
        isSLT: false,
        responsibilities: ['Year 10 Form Group', 'Drama Club'],
      },
      availability: {
        workingDays: {
          Monday: { available: true, startTime: '08:30', endTime: '14:00' },
          Tuesday: { available: true, startTime: '08:30', endTime: '14:00' },
          Wednesday: { available: true, startTime: '08:30', endTime: '14:00' },
          Thursday: { available: true, startTime: '08:30', endTime: '14:00' },
          Friday: { available: false },
        },
        constraints: {
          noBackToBackClasses: true,
          maxConsecutivePeriods: 2,
          preferredBreakTimes: ['10:45'],
          travelTimeBetweenRooms: 10,
        },
      },
      qualifications: [
        {
          subjectId: 'ENG',
          subjectName: 'English Literature',
          level: 'secondary' as const,
          certification: 'MA English Literature',
          dateObtained: '2019-08-10',
          isActive: true,
          canTeachYearGroups: ['Year 7', 'Year 8', 'Year 9', 'Year 10', 'Year 11'],
          confidenceLevel: 4,
        },
      ],
      preferences: {
        preferredSubjects: ['English Literature', 'Creative Writing'],
        preferredYearGroups: ['Year 9', 'Year 10'],
        preferredTimeSlots: ['Period 1', 'Period 2'],
        preferredRooms: ['E01'],
        avoidTimeSlots: ['Period 5', 'Period 6'],
        workloadPreference: 'moderate' as const,
        coTeachingWillingness: false,
        mentorshipAvailability: true,
      },
    },
    {
      id: 'T003',
      personalInfo: {
        firstName: 'Emily',
        lastName: 'Chen',
        email: 'emily.chen@school.edu',
        employeeId: 'EMP003',
        gender: 'female' as const,
      },
      employment: {
        contractType: 'full-time' as const,
        startDate: '2019-08-25',
        teachingCapacity: 20,
        maxPeriodsPerDay: 5,
        totalContactTime: 17,
        salaryScale: 'M4',
      },
      roles: {
        primaryRole: 'Science Teacher',
        additionalRoles: ['Science Technician Coordinator'],
        isHOD: false,
        isSLT: false,
        responsibilities: ['Laboratory Management', 'Science Fair Coordinator'],
      },
      availability: {
        workingDays: {
          Monday: { available: true, startTime: '08:00', endTime: '16:00' },
          Tuesday: { available: true, startTime: '08:00', endTime: '16:00' },
          Wednesday: { available: true, startTime: '08:00', endTime: '16:00' },
          Thursday: { available: true, startTime: '08:00', endTime: '16:00' },
          Friday: { available: true, startTime: '08:00', endTime: '15:30' },
        },
        constraints: {
          noBackToBackClasses: false,
          maxConsecutivePeriods: 4,
          preferredBreakTimes: ['10:45', '13:00'],
          travelTimeBetweenRooms: 5,
        },
      },
      qualifications: [
        {
          subjectId: 'CHEM',
          subjectName: 'Chemistry',
          level: 'secondary' as const,
          certification: 'BSc Chemistry',
          dateObtained: '2017-06-30',
          isActive: true,
          canTeachYearGroups: ['Year 7', 'Year 8', 'Year 9', 'Year 10', 'Year 11'],
          confidenceLevel: 5,
        },
        {
          subjectId: 'BIO',
          subjectName: 'Biology',
          level: 'secondary' as const,
          certification: 'MSc Biochemistry',
          dateObtained: '2018-09-15',
          isActive: true,
          canTeachYearGroups: ['Year 9', 'Year 10', 'Year 11'],
          confidenceLevel: 4,
        },
      ],
      preferences: {
        preferredSubjects: ['Chemistry', 'Biology'],
        preferredYearGroups: ['Year 10', 'Year 11'],
        preferredTimeSlots: ['Period 2', 'Period 3', 'Period 4'],
        preferredRooms: ['SL01', 'SL02'],
        avoidTimeSlots: [],
        workloadPreference: 'moderate' as const,
        coTeachingWillingness: true,
        mentorshipAvailability: false,
      },
    },
  ] as Teacher[],

  assignments: [
    {
      id: 'A001',
      teacherId: 'T001',
      teacherName: 'Sarah Johnson',
      subjectId: 'MATH',
      subjectName: 'Mathematics',
      yearGroup: 'Year 10',
      classGroup: '10A',
      assignmentType: 'primary' as const,
      periodsPerWeek: 5,
      roomName: 'M01',
      effectiveDate: '2024-09-01',
    },
    {
      id: 'A002',
      teacherId: 'T001',
      teacherName: 'Sarah Johnson',
      subjectId: 'MATH',
      subjectName: 'Mathematics',
      yearGroup: 'Year 11',
      classGroup: '11B',
      assignmentType: 'primary' as const,
      periodsPerWeek: 5,
      roomName: 'M01',
      effectiveDate: '2024-09-01',
    },
    {
      id: 'A003',
      teacherId: 'T002',
      teacherName: 'James Wilson',
      subjectId: 'ENG',
      subjectName: 'English Literature',
      yearGroup: 'Year 9',
      classGroup: '9C',
      assignmentType: 'primary' as const,
      periodsPerWeek: 4,
      roomName: 'E01',
      effectiveDate: '2024-09-01',
    },
    {
      id: 'A004',
      teacherId: 'T003',
      teacherName: 'Emily Chen',
      subjectId: 'CHEM',
      subjectName: 'Chemistry',
      yearGroup: 'Year 10',
      classGroup: '10A',
      assignmentType: 'primary' as const,
      periodsPerWeek: 4,
      roomName: 'SL01',
      effectiveDate: '2024-09-01',
    },
  ] as TeacherAssignment[],

  workloadSummary: [
    {
      teacherId: 'T001',
      teacherName: 'Sarah Johnson',
      totalAssignedPeriods: 20,
      contractualPeriods: 22,
      utilizationPercentage: 91,
      status: 'optimal' as const,
      subjectBreakdown: [
        { subjectId: 'MATH', periods: 15, yearGroups: ['Year 10', 'Year 11'] },
        { subjectId: 'STAT', periods: 5, yearGroups: ['Year 11'] },
      ],
    },
    {
      teacherId: 'T002',
      teacherName: 'James Wilson',
      totalAssignedPeriods: 12,
      contractualPeriods: 16,
      utilizationPercentage: 75,
      status: 'underloaded' as const,
      subjectBreakdown: [{ subjectId: 'ENG', periods: 12, yearGroups: ['Year 9', 'Year 10'] }],
    },
    {
      teacherId: 'T003',
      teacherName: 'Emily Chen',
      totalAssignedPeriods: 19,
      contractualPeriods: 20,
      utilizationPercentage: 95,
      status: 'optimal' as const,
      subjectBreakdown: [
        { subjectId: 'CHEM', periods: 12, yearGroups: ['Year 10', 'Year 11'] },
        { subjectId: 'BIO', periods: 7, yearGroups: ['Year 9', 'Year 10'] },
      ],
    },
  ] as WorkloadSummary[],
};
